import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Hand, RefreshCw, ExternalLink } from "lucide-react";

export default function UnassignedPool() {
  const [charts, setCharts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/charts/unassigned")
      .then((r) => { setCharts(r.data); setSelected(new Set()); })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === charts.length) setSelected(new Set());
    else setSelected(new Set(charts.map((c) => c.id)));
  };

  const claim = async () => {
    if (selected.size === 0) return toast.error("Select at least one chart to claim.");
    setSubmitting(true);
    try {
      const r = await api.post("/charts/bulk-claim", { chart_ids: Array.from(selected) });
      toast.success(`Claimed ${r.data.claimed} chart(s) — find them in Assigned Charts`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const claimOne = async (id) => {
    try {
      await api.post(`/charts/${id}/claim`);
      toast.success("Chart claimed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="pickup-pool-page">
      <PageHeader
        title="Pickup Pool"
        subtitle="Unassigned charts waiting for QA. Claim one at a time, or select many and claim in bulk."
        actions={
          <button onClick={load} className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900" data-testid="refresh-btn">
            <RefreshCw size={14}/> Refresh
          </button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          {charts.length} chart{charts.length === 1 ? "" : "s"} in the pool · {selected.size} selected
        </div>
        <button onClick={claim} disabled={submitting || selected.size === 0}
                data-testid="bulk-claim-btn"
                className="inline-flex items-center gap-2 bg-emerald-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-emerald-600 disabled:opacity-60">
          <Hand size={16}/> {submitting ? "Claiming…" : `Claim ${selected.size || ""} chart${selected.size === 1 ? "" : "s"}`.trim()}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading pool…</div>
        ) : charts.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No charts in the pool right now — nice work.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input type="checkbox"
                         checked={selected.size === charts.length && charts.length > 0}
                         onChange={toggleAll}
                         data-testid="select-all-checkbox"
                         className="rounded border-slate-300"/>
                </th>
                <th className="px-3 py-2 text-left">Chart</th>
                <th className="px-3 py-2 text-left">Patient</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">M1021</th>
                <th className="px-3 py-2 text-left">Coder</th>
                <th className="px-3 py-2 text-left">Submitted</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {charts.map((c) => (
                <tr key={c.id} className={`hover:bg-slate-50 ${selected.has(c.id) ? "bg-emerald-50/40" : ""}`} data-testid={`chart-row-${c.chart_id}`}>
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)}
                           data-testid={`chart-checkbox-${c.chart_id}`}
                           className="rounded border-slate-300"/>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{c.chart_id}</td>
                  <td className="px-3 py-2.5">{c.patient_name}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{c.assessment_type}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">
                    {c.m1021_primary ? <span className="bg-sky-50 text-sky-700 rounded px-1.5 py-0.5">{c.m1021_primary}</span> : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{c.submitted_by_name}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{c.submitted_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => claimOne(c.id)} data-testid={`claim-one-${c.chart_id}`}
                              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 rounded px-2 py-1">
                        <Hand size={11}/> Claim
                      </button>
                      <Link to={`/charts/${c.id}`} className="text-slate-400 hover:text-sky-700" data-testid={`chart-view-${c.chart_id}`}>
                        <ExternalLink size={14}/>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
